// Account context — mirrors the IG Field Suite's ProjectContext pattern. The
// URL (/accounts/:accountId/...) is the source of truth; AccountProvider fetches the
// account from the API and mirrors the id to localStorage (per workspace, see lib/lastAccount) as `lastAccountId` so a
// bare /accounts URL (or a legacy flat module URL) can bounce to the most
// recently used account.
//
// Anything account-scoped in the future (LiveCall, Onboarding, Fulfillment,
// Portal admin per-account) is expected to live under this context and read
// useAccount() rather than passing account ids down the tree.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/state/auth";
import { readLastAccount, writeLastAccount } from "@/lib/lastAccount";

export type Account = {
  id: number;
  name: string;
  owner_name: string | null;
  phone: string | null;
  email: string | null;
  city: string | null;
  state: string | null;
  status: string;
  shop_type: string | null;
  lead_source: string | null;
  monthly_rev_cents: number | null;
  avg_ticket_cents: number | null;
  website: string | null;
  company: string | null;
  research_json: string | null;
  research_status: string | null;
  updated_at: string;
};
export type AccountBundle = {
  account: Account;
  bookings: any[];
  calls: any[];
  subscriptions: any[];
  dials: number;
};

type State = {
  bundle: AccountBundle | null | undefined; // undefined = loading, null = not found
  error: string | null;
  reload: () => void;
};

const Ctx = createContext<State | null>(null);

export function AccountProvider({ children }: { children: ReactNode }) {
  const { accountId } = useParams<{ accountId: string }>();
  const ws = useAuth().activeAgency?.id ?? null;
  const [bundle, setBundle] = useState<AccountBundle | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // Blank to the loading state ONLY when the account itself changes — not on
  // a reload()/poll, which would flash the page. Reloads refetch in place.
  useEffect(() => {
    if (!accountId) { setBundle(null); return; }
    setBundle(undefined); setError(null);
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    api.get<AccountBundle>(`/accounts/${accountId}`)
      .then((b) => { if (!cancelled) { setBundle(b); setError(null); writeLastAccount(ws, b.account.id); } })
      .catch((e) => {
        if (cancelled) return;
        // On a reload/poll failure keep whatever we already have (don't blank
        // the whole page). Only fall to "not found" if we never loaded.
        setBundle((prev) => (prev === undefined ? null : prev));
        setError(e instanceof Error ? e.message : "Account not found");
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, nonce]);

  return (
    <Ctx.Provider value={{ bundle, error, reload: () => setNonce((n) => n + 1) }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAccount(): State {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAccount must be used inside AccountProvider (under /accounts/:accountId)");
  return c;
}

/** Last-used account in workspace `ws` (per-workspace memory). */
export function lastAccountId(ws: string | null | undefined): number | null {
  return readLastAccount(ws);
}
